import { Navbar } from "../Navigation/Navbar";
import { HelpButton } from "../Common/HelpButton";
import { Button, Dropdown, Typography, Card, Modal, message, Tooltip } from "antd";
import { CaretDownOutlined, DownOutlined, FilePdfOutlined } from "@ant-design/icons";
import { lazy, Suspense, useEffect, useState } from "react";
import "../../Styles/styles-Reports.css";
import { useSelector } from "react-redux";
import { MultiMetricPicker } from "../Common/MultiMetricPicker.tsx";
import { IGeneralReportsConfig, REPORT_TYPES } from "./constants";
import {
    countFilledMetrics,
    formatDuplicateMetricsMessage,
    formatIncompleteMetricsMessage,
    getDuplicateMetricGroups,
    getIncompleteMetricGroups,
    isMetricSlotFilled,
} from "@utils/report_metric_utils";
import GeneralReportConfig from "./GeneralReportConfig";
import { PresetType } from "../PresetSelection.tsx";
import { getTeamPresets, setTeamPresets } from "../../store/actions/team.actions.js";
import { useDispatch } from "react-redux";
import { SelectWithAdd } from "../Common/SelectWithAdd.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from 'react-router-dom';
import { trainingAPI } from "../../api/TrainingAPI.js";
import dayjs from "dayjs";
const PdfAnnotatorModal = lazy(() =>
  import("@components/PDFAnnotator/PdfAnnotatorModal").then((m) => ({ default: m.PdfAnnotatorModal })),
);

const { Text } = Typography;

export const GeneralReports = () => {
    const dispatch = useDispatch();
    const [loadingMode, setLoadingMode] = useState<null | 'email' | 'pdf' | 'annotate'>(null);
    const [pdfAnnotatorOpen, setPdfAnnotatorOpen] = useState<boolean>(false);
    const [annotatorFile, setAnnotatorFile] = useState<{ buffer: ArrayBuffer; name: string } | null>(null);

    const {
        info: {
            display_name: team_name,
        },
        presets
    } = useSelector(({ team }) => team);
    const { user } = useAuth0();

    const [selectedReportTypeKey, setSelectedReportTypeKey] = useState<string | undefined>(undefined);
    const [metricSelectorModalIsOpen, setMetricSelectorModalIsOpen] = useState<boolean>(false);
    const [useAggs, setUseAggs] = useState<boolean>(false);
    const [metricSelectorPrefix, setMetricSelectorPrefix] = useState<string | undefined>(undefined);

    const getFilteredReportList = () => {
        return REPORT_TYPES.filter((item) => {
            if (item.teams && item.teams.length > 0) {
                return item.teams.includes(team_name);
            }
            return true;
        })
    }

    const presetItemsOfInterest = presets?.filter((e: any) => e.type === PresetType.ReportSettings)
    const reportPresetItems = presetItemsOfInterest?.map((preset: any) => {
        return {
            label: preset.name,
            value: preset.name,
            // Any staff user can use a preset. Only its creator can save over it or delete it.
            disabled: false,
            deletable: preset.creator_id__email === user?.email,
        }
    })
    const getPresetSelected = () => presetItemsOfInterest?.find((preset: any) => preset.name === currentSelectedTilePreset)

    const [searchParams] = useSearchParams();

    const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState<string | undefined>(undefined)
    const [config, setConfig] = useState<IGeneralReportsConfig>(getPresetSelected()?.config || {});

    const addPreset = async (item: string) => {
        const newPreset = await trainingAPI.createPreset({ name: item, type: PresetType.ReportSettings, config });
        dispatch(setTeamPresets(newPreset))

        setCurrentSelectedTilePreset(item)
    }

    const deletePreset = async (item: string) => {
        const presetOfInterest = presetItemsOfInterest?.find((preset: any) => preset.name === item)
        await trainingAPI.deletePreset(presetOfInterest?.preset_id)

        const updatedPreset = presets.filter((preset: any) => preset.preset_id !== presetOfInterest?.preset_id)
        dispatch(setTeamPresets(updatedPreset))

        setCurrentSelectedTilePreset((updatedPreset && updatedPreset?.length > 0) ? updatedPreset[0].value : undefined)
    }

    const updatedPreset = async () => {
        const presetModified = presetItemsOfInterest?.find((preset: any) => preset.name === currentSelectedTilePreset)
        await trainingAPI.updatePreset({
            name: currentSelectedTilePreset, type: PresetType.ReportSettings, config, preset_id: presetModified?.preset_id
        });
        presetModified.config = config
        dispatch(setTeamPresets(presets))
        message.success("Saved!");
    }


    useEffect(() => {
        dispatch(getTeamPresets({
            type: [
                PresetType.CustomBreakdownReportMetrics,
                PresetType.GpsReportMetrics,
                PresetType.TeamReportMetrics,
                PresetType.PlayerReportMetrics,
                PresetType.TestingReportMetrics,
                PresetType.ForcePlateReportMetrics,
                PresetType.ReportSettings
            ]
        }) as any)
    }, [])

    useEffect(() => {
        if (currentSelectedTilePreset === undefined) {
            setConfig({})
            setSelectedReportTypeKey(undefined)
        } else {
            setConfig(getPresetSelected()?.config || { ...config })
            // change report type on preset change
            setSelectedReportTypeKey((getPresetSelected()?.config || { ...config })?.report_type)
        }
    }, [currentSelectedTilePreset])

    useEffect(() => {
        const presetParam = searchParams.get('preset');
        if (!presetParam || !presets?.length) return;
        const matchingPreset = presets.find(
            (p: any) => p.type === PresetType.ReportSettings && p.name === presetParam
        );
        if (matchingPreset) {
            setCurrentSelectedTilePreset(presetParam);
        }
    }, [presets]);

    const menu = {
        items: getFilteredReportList(),
        onClick: (value: any) => {
            const newConfig: any = { report_type: value.key };

            if (value.key === 'player_date_range_report') {
                newConfig.aggregate_option = 'avg';
                newConfig.aggregate_columns = [
                    'player_value',
                    'position_value',
                    'team_value',
                    'player_zscore',
                    'player_tscore',
                    'player_tscore_desc',
                    'player_percentile'
                ];
            }
            
            setConfig(newConfig);
            setSelectedReportTypeKey(value.key);
        },
    };

    const getSelectReportTypeObj = (key?: string) => {
        return REPORT_TYPES.find(x => x.key === key) || undefined;
    }

    const setConfigMetrics = (metric_list: any) => {
        setConfig((prev) => ({
            ...prev,
            [`${metricSelectorPrefix}-metrics`]: metric_list
        }));
    }

    // Unified report generation method
    const handleUnifiedReportSubmit = async (mode: 'email' | 'pdf' | 'annotate' = 'email') => {
        if (!selectedReportTypeKey) {
            message.error("Please select a report type");
            return;
        }
        

        const reportObj = getSelectReportTypeObj(selectedReportTypeKey);

        if (!reportObj) {
            message.error("Invalid report type selected");
            return;
        }

        // Validate required parameters based on report type
        if (reportObj.requirements?.includes('date') && !config.date) {
            message.error("Please select a date for the report");
            return;
        }

        if (reportObj.requirements?.includes('date_range') && (!config.date_range || config.date_range.length !== 2)) {
            message.error("Please select a date range for the report");
            return;
        }

        if (reportObj.requirements?.includes('single_player_filter') && !config.player_filter) {
            message.error("Please select a player for the report");
            return;
        }

        if (reportObj.requirements?.includes('player_report_type_selector') && (!config.player_report_data_streams || config.player_report_data_streams.length === 0)) {
            message.error("Please select at least one data stream for the player report");
            return;
        }

        if (reportObj.requirements?.includes('dual_date_ranges')) {
            if (!config.dual_date_ranges || config.dual_date_ranges.length !== 2) {
                message.error("Please select two date ranges for the report");
                return;
            }
            if (!config.dual_date_ranges[0] || config.dual_date_ranges[0].length !== 2 || !config.dual_date_ranges[0][0] || !config.dual_date_ranges[0][1]) {
                message.error("Please complete Date Range 1");
                return;
            }
            if (!config.dual_date_ranges[1] || config.dual_date_ranges[1].length !== 2 || !config.dual_date_ranges[1][0] || !config.dual_date_ranges[1][1]) {
                message.error("Please complete Date Range 2");
                return;
            }
        }

        if (reportObj.requirements?.includes('subjects_filter')) {
            // Check if at least one subject type is selected
            const hasPlayers = config.player_filter && config.player_filter.length > 0;
            const hasPositions = config.position_filter && config.position_filter.length > 0;
            const hasGroups = config.player_group_filter && config.player_group_filter.length > 0;
            
            if (!hasPlayers && !hasPositions && !hasGroups) {
                message.error("Please select at least one subject (player, position, player group, or full-team)");
                return;
            }
        }

        if (reportObj.requirements?.includes('aggregate_selector')) {
            if (!config.aggregate_option) {
                message.error("Please select an aggregate method (Avg or Best)");
                return;
            }
        }

        if (reportObj.requirements?.includes('aggregate_columns_selector')) {
            if (!config.aggregate_columns || config.aggregate_columns.length === 0) {
                message.error("Please select at least one aggregate column");
                return;
            }
            if (config.aggregate_columns.length > 7) {
                message.error("Please select no more than 7 aggregate columns");
                return;
            }
        }

        if (selectedReportTypeKey === 'percent_of_best_analysis' && config.show_briefing_notes && (!config.briefing_notes_metrics || config.briefing_notes_metrics.length === 0)) {
            message.error("Please select at least one metric for the briefing notes");
            return;
        }

        // Prepare the payload for the unified API
        const payload: any = {
            ...config,
            report_type: selectedReportTypeKey,
            emails: mode === 'email' ? config.emails : [], // Only include emails for email mode
            mode: mode === 'annotate' ? 'pdf' : mode, // annotate also generates a pdf on the backend
        };
        const selectedMetrics = (config as any)[`${selectedReportTypeKey}-metrics`]

        if (selectedReportTypeKey === 'player_date_range_report') {
            // Handles both formats: string array (new) or object array (old)
            const filledMetricCount = countFilledMetrics(selectedMetrics);

            if (filledMetricCount === 0) {
                message.error('Please select at least one metric');
                return;
            }
            if (filledMetricCount > 15) {
                message.error('Please select no more than 15 metrics');
                return;
            }
        } else {
            // Every fixed-size metric selector — single-selector reports as well as the
            // per-data-stream selectors of the player and weekly reports — must be
            // filled completely before the report is generated.
            const incompleteMetricGroups = getIncompleteMetricGroups(selectedReportTypeKey, config);
            if (incompleteMetricGroups.length > 0) {
                message.error(formatIncompleteMetricsMessage(incompleteMetricGroups));
                return;
            }

            const duplicateMetricGroups = getDuplicateMetricGroups(selectedReportTypeKey, config);
            if (duplicateMetricGroups.length > 0) {
                message.error(formatDuplicateMetricsMessage(duplicateMetricGroups));
                return;
            }
        }

        // Recipients are validated last so that incomplete selections are always
        // reported first. Only relevant when the report is emailed.
        if (mode === 'email' && (!config.emails || config.emails.length === 0)) {
            message.error("Please select at least one email recipient");
            return;
        }

        // Handle special case for weekly body weight report
        if (selectedReportTypeKey === 'weekly_body_weight_report') {
            payload.report_type = 'body_weight_report';  // Use same backend handler
            payload.weekly = true;  // Enable weekly mode
        }

        // Weekly report: send date_range as start_date and end_date
        if (selectedReportTypeKey === 'weekly') {
            payload.report_type = 'weekly';
            // Convert date_range to start_date and end_date for backend
            if (config.date_range && config.date_range.length === 2) {
                payload.start_date = config.date_range[0];
                payload.end_date = config.date_range[1];
                // Also keep date_range for compatibility
                payload.date_range = config.date_range;
            }
            // Map weekly_streams to data_types for backend
            const weekly_streams = (config as any).weekly_streams || [];
            payload.data_types = {
                gps: weekly_streams.includes('gps'),
                testing: weekly_streams.includes('testing'),
                forceplate: weekly_streams.includes('forceplate'),
                wellness: weekly_streams.includes('wellness'),
            };
        }

        // Transform metrics data for the API
        if (selectedReportTypeKey === 'weekly') {
            // For weekly reports, we need separate metric arrays. A de-selected stream
            // keeps its config entry, which may hold half-finished slots — drop those.
            const filledMetrics = (stream: string) =>
                ((config as any)[`${stream}-metrics`] || []).filter(isMetricSlotFilled);

            payload.gps_metrics = filledMetrics('gps');
            payload.testing_metrics = filledMetrics('testing');
            payload.forceplate_metrics = filledMetrics('forceplate');
        } else if (selectedMetrics) {
            payload.metrics = selectedMetrics;
        }

        // Handle player_date_range_report transformations
        if (selectedReportTypeKey === 'player_date_range_report') {
            payload.date_ranges = config.dual_date_ranges;
            
            // Build subjects array from separate filters
            const subjects: string[] = [];
            if (config.player_filter && config.player_filter.length > 0) {
                config.player_filter.forEach((playerId: any) => {
                    if (playerId === 'full-team') {
                        subjects.push('full-team');
                    } else {
                        subjects.push(`player-${playerId}`);
                    }
                });
            }
            if (config.position_filter && config.position_filter.length > 0) {
                config.position_filter.forEach((pos: string) => {
                    subjects.push(`pos-${pos}`);
                });
            }
            if (config.player_group_filter && config.player_group_filter.length > 0) {
                config.player_group_filter.forEach((groupId: any) => {
                    subjects.push(`group-${groupId}`);
                });
            }
            payload.subjects = subjects;
            
            payload.agg = config.aggregate_option;
            payload.aggregates = config.aggregate_columns;
            
            if (selectedMetrics && selectedMetrics.length > 0) {
                payload.metrics = selectedMetrics;
            }
        }

        // Handle player report data streams metrics
        if (config.player_report_data_streams) {
            const dataStreamMetrics: any = {};
            config.player_report_data_streams.forEach((stream) => {
                if ((config as any)[`${stream}-metrics`]) {
                    dataStreamMetrics[stream] = (config as any)[`${stream}-metrics`];
                }
            });
            payload.player_report_metrics = dataStreamMetrics;
        }

        // Handle sessions parameter (needed for GPS reports)
        if ((config as any).sessions) {
            payload.sessions = (config as any).sessions;
        }

        setLoadingMode(mode);
        try {
            if (mode === 'annotate') {
                const { blob, filename } = await trainingAPI.generateGeneralReport({ ...payload, returnBlob: true });
                setAnnotatorFile({ buffer: await blob.arrayBuffer(), name: filename || 'report.pdf' });
                setPdfAnnotatorOpen(true);
            } else {
                await trainingAPI.generateGeneralReport(payload);
                message.success(mode === 'email' ? "The report has been sent to your email" : "Report downloaded successfully");
            }
        } catch (error) {
            console.error("Report generation error:", error);
            message.error(typeof error === 'string' ? error : "Failed to generate report. Please try again.");
        } finally {
            setLoadingMode(null);
        }
    };

    const handleSubmit = (mode: 'email' | 'pdf' | 'annotate' = 'email') => {
        if (selectedReportTypeKey === 'single_week_distance_breakdown' && dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') > 7) {
            message.error(`Date range cannot exceed 7 days. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        } else if (selectedReportTypeKey === 'multi_week_distance_breakdown' && dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') > 49) {
            message.error(`Date range cannot exceed 7 weeks. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        } else if (selectedReportTypeKey === 'status_report') {
            if (!config.date_range) {
                message.error("Please select a date for the report");
                return;
            }

            const daysDiff = dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') + 1; // +1 to include both start and end dates
            if (daysDiff < 1 || daysDiff > 266) {
                message.error(`Status report date range must be between 1 and 266 days. Provided range is ${daysDiff} days: ${config.date_range[0]} to ${config.date_range[1]}`)
                return;
            }
        } else if (selectedReportTypeKey === 'weekly_body_weight_report' && dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') > 84) {
            message.error(`Date range cannot exceed 12 weeks. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        } else if (selectedReportTypeKey === 'weekly') {
            if (!config.date_range || config.date_range.length !== 2 || !config.date_range[0] || !config.date_range[1]) {
                message.error('Please select a date range for the weekly report');
                return;
            }
            const daysDiff = dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') + 1; // +1 to include both start and end dates
            if (daysDiff !== 7) {
                message.error(`Weekly report requires exactly 7 days. Provided range is ${daysDiff} days: ${config.date_range[0]} to ${config.date_range[1]}`)
                return;
            }
        } else if (selectedReportTypeKey === 'report-card-bulk' && dayjs(config.date_range[1]).diff(dayjs(config.date_range[0]), 'days') > 90) {
            message.error(`Date range cannot exceed 90 days. Provided range: ${config.date_range[0]} to ${config.date_range[1]}`)
            return;
        }

        // Use the unified report submit by default
        // You can switch to handleLegacyReportSubmit() for testing legacy functionality
        handleUnifiedReportSubmit(mode);
    };

    return (
        <>
            <Navbar />
            <HelpButton page="Reports" />

            <Card
                title={<Text style={{ fontSize: 22 }}>Generate a Report</Text>}
                className="general-report-options-container"
                // extra={
                //     <Button icon={<FilePdfOutlined />} onClick={() => setPdfAnnotatorOpen(true)}>
                //         Annotate a PDF
                //     </Button>
                // }
            >
                <>
                    <div style={{ width: '700px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <div style={{ flexGrow: 1 }} className="preset-row">
                            <SelectWithAdd
                                items={reportPresetItems || []}
                                addItem={addPreset}
                                deleteItem={deletePreset}
                                placeholder="Select Preset"
                                setValue={setCurrentSelectedTilePreset}
                                currentSelectedTilePreset={currentSelectedTilePreset || undefined}
                                addItemLabel={"Add"}
                                addItemTextboxLabel={"Enter Preset Name"}
                                label={"Would you like to select a pre-configured report?"}
                                labelPaddingRight={10}
                                allowClear={true}
                            />
                        </div>

                        <div>
                            <Button type="primary" size="small"
                                onClick={updatedPreset}
                                disabled={!!getPresetSelected() && getPresetSelected().creator_id__email !== user?.email}
                                title={!!getPresetSelected() && getPresetSelected().creator_id__email !== user?.email ? "Cannot modify a preset created by another user" : undefined}
                                style={{ width: "100px" }}
                                className={''}
                            >
                                Save Preset
                            </Button>
                        </div>
                    </div>
                </>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>Please select the type of report and date you would like to generate: </span>
                    <div style={{ marginLeft: '10px' }}>
                        <Dropdown menu={menu} className="general-report-type-dropdown">
                            <Button
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {getSelectReportTypeObj(selectedReportTypeKey)?.label || "Select Report Type"} <CaretDownOutlined />
                            </Button>
                        </Dropdown>
                    </div>
                </div>

                <GeneralReportConfig
                    selectedReportTypeKey={selectedReportTypeKey}
                    getSelectReportTypeObj={getSelectReportTypeObj}
                    config={config}
                    setConfig={setConfig}
                    setMetricSelectorModalIsOpen={setMetricSelectorModalIsOpen}
                    setMetricSelectorPrefix={setMetricSelectorPrefix}
                    setUseAggs={setUseAggs}
                />

                <div style={{ float: 'right', marginTop: '10px', display: 'flex', gap: '10px' }}>
                    {getSelectReportTypeObj(selectedReportTypeKey)?.show_download && (
                        <Dropdown
                            menu={{
                                items: [
                                    { key: 'download', label: 'Download Only' },
                                    { key: 'annotate', label: 'Annotate + Download' },
                                ],
                                onClick: (info) => handleSubmit(info.key === 'annotate' ? 'annotate' : 'pdf'),
                            }}
                            placement="bottomRight"
                            disabled={loadingMode !== null}
                        >
                            <Button
                                type="default"
                                loading={loadingMode === 'pdf' || loadingMode === 'annotate'}
                                disabled={loadingMode !== null}
                            >
                                Download Now <DownOutlined />
                            </Button>
                        </Dropdown>
                    )}
                    <Button
                        type="primary"
                        onClick={() => handleSubmit('email')}
                        loading={loadingMode === 'email'}
                        disabled={loadingMode !== null}
                    >
                        Generate Report
                    </Button>
                </div>
            </Card>

            {metricSelectorModalIsOpen && (
                <Modal
                    title={`Configure Metrics: ${getSelectReportTypeObj(selectedReportTypeKey)?.label}`}
                    width={"60%"}
                    open={metricSelectorModalIsOpen}
                    onCancel={() => setMetricSelectorModalIsOpen(false)}
                    footer={null}
                >
                    <MultiMetricPicker
                        requirements={getSelectReportTypeObj(selectedReportTypeKey)}
                        setResult={setConfigMetrics}
                        noAsym={true}
                        setOpen={setMetricSelectorModalIsOpen}
                        showAggs={useAggs}
                        presetInfo={{ items: presets }}
                        config={config}
                        metricSelectorPrefix={metricSelectorPrefix}
                    />
                </Modal>

            )
            }

            {pdfAnnotatorOpen && (
                <Suspense fallback={null}>
                    <PdfAnnotatorModal
                        open
                        initialFile={annotatorFile ?? undefined}
                        onClose={() => {
                            setPdfAnnotatorOpen(false);
                            setAnnotatorFile(null);
                        }}
                    />
                </Suspense>
            )}
        </>
    )
}

export default GeneralReports